import { SidePanelInterface } from '../types';
import SidePanel from './SidePanel';
import React, { useEffect, useState } from 'react';
import { CreateButton, FormGroup, InputField, Label, ModalTitle } from './SharedComponents';
import apiClient from '../services/apiClient';
import { CircularProgress, Chip, Box } from '@mui/material';
import notify from '../services/notify';
import ReactGA from 'react-ga4';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from './ConfirmationModal';
import ManageCustomersModal from './ManageCustomersModal';
import PersonIcon from '@mui/icons-material/Person';

interface CustomerGroupModalProps extends Omit<SidePanelInterface, 'children'> {
	customerGroup?: any | null;
	handleHidePanel: (customerGroup?: any | null) => void;
	isNewCustomerGroup?: boolean;
	onCustomerGroupUpdate?: (customerGroup: any) => void;
	onCustomerGroupDelete?: (customerGroupId: number) => void;
}

interface CustomerGroupFormData {
	name: string;
	description: string;
}

interface Customer {
	id: number;
	name: string;
	email?: string;
	phone?: string;
	address?: string;
}

export default function CustomerGroupModal(props: CustomerGroupModalProps) {
	const {
		customerGroup,
		handleHidePanel,
		isNewCustomerGroup,
		onCustomerGroupUpdate,
		onCustomerGroupDelete,
		...sidePanelProps
	} = props;

	const [formData, setFormData] = useState<CustomerGroupFormData>({
		name: '',
		description: '',
	});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isManageCustomersOpen, setIsManageCustomersOpen] = useState<boolean>(false);
	const [customersInGroup, setCustomersInGroup] = useState<Customer[]>([]);
	const [isLoadingCustomers, setIsLoadingCustomers] = useState<boolean>(false);

	useEffect(() => {
		if (customerGroup) {
			setFormData({
				name: customerGroup.name || '',
				description: customerGroup.description || '',
			});

			// Fetch customers in this group if it's an existing group
			if (customerGroup.id) {
				fetchCustomersInGroup();
			}
		} else {
			setFormData({
				name: '',
				description: '',
			});
			setCustomersInGroup([]);
		}
	}, [customerGroup]);

	const fetchCustomersInGroup = async () => {
		if (!customerGroup?.id) return;

		setIsLoadingCustomers(true);
		try {
			const response = await apiClient.get(`/customer-groups/${customerGroup.id}/customers`);
			setCustomersInGroup(response.data || []);
		} catch (error) {
			console.error('Error fetching customers in group:', error);
			notify('error', 'Failed to load customers in group');
		} finally {
			setIsLoadingCustomers(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.name.trim()) {
			notify('error', 'Group name is required');
			return;
		}

		setIsSubmitting(true);

		try {
			let response;
			if (isNewCustomerGroup) {
				response = await apiClient.post('/customer-groups', formData);
				notify('success', 'Customer group created successfully');
				ReactGA.event({
					category: 'Customer Groups',
					action: 'Created Customer Group',
					label: 'New Customer Group',
				});
			} else {
				response = await apiClient.update(`/customer-groups/${customerGroup.id}`, formData);
				notify('success', 'Customer group updated successfully');
				if (onCustomerGroupUpdate) {
					onCustomerGroupUpdate(response.data);
				}
				ReactGA.event({
					category: 'Customer Groups',
					action: 'Updated Customer Group',
					label: `Customer Group ID: ${customerGroup.id}`,
				});
			}

			handleHidePanel(response.data);
		} catch (error: any) {
			console.error('Error saving customer group:', error);
			notify('error', error.response?.data?.message || 'Failed to save customer group');
			ReactGA.event({
				category: 'Customer Groups',
				action: 'Failed to Save Customer Group',
				label: isNewCustomerGroup ? 'New Customer Group' : `Customer Group ID: ${customerGroup?.id}`,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!customerGroup?.id) return;

		setIsDeleting(true);
		try {
			await apiClient.delete(`/customer-groups/${customerGroup.id}`);
			notify('success', 'Customer group deleted successfully');

			if (onCustomerGroupDelete) {
				onCustomerGroupDelete(customerGroup.id);
			}

			ReactGA.event({
				category: 'Customer Groups',
				action: 'Deleted Customer Group',
				label: `Customer Group ID: ${customerGroup.id}`,
			});

			setIsDeleteDialogOpen(false);
			handleHidePanel(null);
		} catch (error: any) {
			console.error('Error deleting customer group:', error);
			notify('error', error.response?.data?.message || 'Failed to delete customer group');

			ReactGA.event({
				category: 'Customer Groups',
				action: 'Failed to Delete Customer Group',
				label: `Customer Group ID: ${customerGroup.id}`,
			});
		} finally {
			setIsDeleting(false);
		}
	};

	const handleCustomersUpdated = () => {
		// Refresh the customers list
		fetchCustomersInGroup();
		// Also update the parent component's customer count
		if (onCustomerGroupUpdate && customerGroup) {
			onCustomerGroupUpdate({
				...customerGroup,
				customer_count: customersInGroup.length,
			});
		}
	};

	const headerComponents: any[] = [];

	// Only show delete button for existing customer groups
	if (!isNewCustomerGroup && customerGroup?.id) {
		headerComponents.push(
			<CreateButton onClick={() => setIsManageCustomersOpen(true)} outline>
				<PersonIcon style={{ fontSize: '16px' }} />
				<span>Manage Customers</span>
			</CreateButton>
		);
	}

	return (
		<>
			<SidePanel
				{...sidePanelProps}
				handleHidePanel={() => handleHidePanel()}
				headerComponents={headerComponents}
			>
				<ModalTitle>{isNewCustomerGroup ? 'Create' : 'Edit'} Customer Group</ModalTitle>

				<form onSubmit={handleSubmit}>
					<FormGroup>
						<Label>Group Name *</Label>
						<InputField
							type="text"
							name="name"
							value={formData.name}
							onChange={handleInputChange}
							placeholder="Enter group name"
							required
						/>
					</FormGroup>

					<FormGroup>
						<Label>Description</Label>
						<InputField
							type="text"
							name="description"
							value={formData.description}
							onChange={handleInputChange}
							placeholder="Enter group description (optional)"
							style={{ minHeight: '80px' }}
							as="textarea"
						/>
					</FormGroup>

					<CreateButton type="submit" disabled={isSubmitting} style={{ marginTop: '20px', width: '100%' }}>
						{isSubmitting && <CircularProgress size={15} color="inherit" style={{ marginRight: '8px' }} />}
						<span>{isNewCustomerGroup ? 'Create Group' : 'Update Group'}</span>
					</CreateButton>
				</form>

				{/* Customers Section - Only show for existing groups */}
				{!isNewCustomerGroup && customerGroup?.id && (
					<>
						<hr
							style={{
								margin: '40px 0px 30px 0px',
								backgroundColor: '#f3f3f3',
								border: 'none',
								height: '1px',
							}}
						/>

						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								marginBottom: '20px',
							}}
						>
							<h4 style={{ margin: 0 }}>Customers in Group</h4>
						</div>

						{isLoadingCustomers ? (
							<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
								<CircularProgress size={24} />
							</div>
						) : customersInGroup.length > 0 ? (
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
								{customersInGroup.map((customer) => (
									<Chip
										key={customer.id}
										label={customer.name}
										variant="outlined"
										style={{
											borderColor: '#3a5bd8',
											color: '#3a5bd8',
											backgroundColor: '#f5f8ff',
										}}
									/>
								))}
							</div>
						) : (
							<div
								style={{
									textAlign: 'center',
									padding: '20px',
									color: '#6b7280',
									fontSize: '14px',
									fontStyle: 'italic',
								}}
							>
								No customers in this group yet. Click "Manage Customers" to add customers.
							</div>
						)}
						<Box sx={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
							<CreateButton
								key="delete-button"
								onClick={() => setIsDeleteDialogOpen(true)}
								outline
								style={{ borderColor: '#dc2626', color: '#dc2626', width: '100%' }}
							>
								<DeleteIcon style={{ fill: '#dc2626' }} />
								<span>Delete Customer Group</span>
							</CreateButton>
						</Box>
					</>
				)}

				<ConfirmationModal
					isOpen={isDeleteDialogOpen}
					title="Delete Customer Group"
					message={`Are you sure you want to delete the customer group "${customerGroup?.name}"? This action cannot be undone. Customers in this group will no longer be assigned to any group.`}
					confirmText="Delete Group"
					cancelText="Cancel"
					onConfirm={handleDelete}
					onCancel={() => setIsDeleteDialogOpen(false)}
					isLoading={isDeleting}
					variant="danger"
				/>
			</SidePanel>

			{/* Manage Customers Modal */}
			{isManageCustomersOpen && (
				<ManageCustomersModal
					isOpen={isManageCustomersOpen}
					onClose={() => setIsManageCustomersOpen(false)}
					customerGroup={customerGroup}
					onCustomersUpdated={handleCustomersUpdated}
				/>
			)}
		</>
	);
}
